import { Fragment, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { errorMessage } from "@/app/api";
import { ROLE_LABEL, useEnvLabel, useLogout, useMe } from "@/app/auth";
import { Brand, BrandMark } from "@/app/Brand";
import { findNav, visibleNav } from "@/app/nav";
import { NavIcon } from "@/app/navIcons";
import { PasswordDrawer } from "@/app/PasswordDrawer";
import { TaskCenterButton, TaskCenterProvider } from "@/app/TaskCenter";
import { ThemeSwitch } from "@/app/ThemeSwitch";
import { useSidebar } from "@/app/useSidebar";
import { Crumbs, Dropdown, Main, NavGroup, NavItem, Shell, Sidebar, Topbar, useToast } from "@/ui";



// 内容区的最小宽度：窗口再窄就在内容区里横向滚动，不把路由计划的明细栏、表格与工具栏挤得错位。
// 加在应用层而不改设计系统的 .ax-main（ui.css 与设计系统逐字一致）；.ax-main 本身 overflow:auto，滚动条出在内容区里
const contentMin = { minWidth: 1120 };
export function Layout() {
  const { pathname } = useLocation();
  const hit = findNav(pathname);
  const side = useSidebar(hit?.group.title);
  const me = useMe().data;
  const dev = useEnvLabel() !== "";
  const logout = useLogout();
  const toast = useToast();
  const [changingPassword, setChangingPassword] = useState(false);
  // 服务端会话删不掉也照样退出本地会话（见 useLogout），只提示一下
  const onLogout = () => logout().catch((e: unknown) => toast(`退出登录时出错：${errorMessage(e)}`, { tone: "danger" }));

  // 顶栏右侧：主题、任务中心（图标按钮，紧挨用户名左侧）、当前用户；时间一律北京时间，不再标注 UTC+8（决策 D205）
  return (
    <TaskCenterProvider>
    <Shell>
      <Sidebar brand={<Brand />} brandCollapsed={<BrandMark />} collapsed={side.collapsed} onCollapsedChange={side.setCollapsed}>
        {visibleNav(me?.role, dev).map((g) => (
          // 分组标题与导航项是同级的兄弟节点（与设计系统的 AppShell 一致），分组间的分隔线靠 :first-child 判断第一组。
          // 图标条里没有分组标题可点，分组一律展开
          <Fragment key={g.title}>
            <NavGroup open={side.isOpen(g.title)} onToggle={() => side.toggle(g.title)}>{g.title}</NavGroup>
            {(side.collapsed || side.isOpen(g.title)) && g.items.map((it) => (
              <NavItem key={it.path} to={it.path} icon={<NavIcon name={it.icon} />} label={it.label} />
            ))}
          </Fragment>
        ))}
      </Sidebar>
      <Topbar crumbs={<Crumbs items={hit ? [hit.group.title, hit.item.label] : ["Axon"]} />} right={<><ThemeSwitch /><TaskCenterButton />{me && <UserMenu name={me.username} role={ROLE_LABEL[me.role]} onPassword={() => setChangingPassword(true)} onLogout={() => void onLogout()} />}</>} />
      {me && changingPassword && <PasswordDrawer username={me.username} onClose={() => setChangingPassword(false)} />}
      <Main>
        <div style={contentMin}><Outlet /></div>
      </Main>
    </Shell>
    </TaskCenterProvider>
  );
}

/** 顶栏的当前用户：ghost 小号的菜单，显示角色并提供修改密码（D201）与退出登录（设计系统 AppShell）。 */
function UserMenu({ name, role, onPassword, onLogout }: { name: string; role: string; onPassword: () => void; onLogout: () => void }) {
  return <Dropdown label={name} variant="ghost" size="sm" align="end" items={[{ heading: role }, "sep", { key: "password", label: "修改密码", onSelect: onPassword }, { key: "logout", label: "退出登录", onSelect: onLogout }]} />;
}
